import { gql } from '@apollo/client'
import {
	EMERGENCY_CONTACT_FRAGMENT,
	IMAGE_FILE_FRAGMENT,
	JOB_APPLICANT_FRAGMENT,
	JOB_DETAIL_GENERAL_FRAGMENT,
	JOB_FRAGMENT,
	MAESTRO_EMPLOYER_FRAGMENT,
	RECRUITER_JOB_BASE_FRAGMENT,
	RECRUITER_JOB_FRAGMENT,
	SALESFORCE_INTEGRATION_FRAGMENT,
	WORK_EXPERIENCE_FRAGMENT,
	WORKED_TIMESHEET_FRAGMENT,
	WORKER_ASSIGNMENT_FRAGMENT,
	WORKER_CERTIFICATION_FRAGMENT,
	WORKER_CREDENTIAL_FRAGMENT,
	WORKER_DISCIPLINE_SPECIALTY_FRAGMENT,
	WORKER_EDUCATION_FRAGMENT,
	WORKER_FRAGMENT,
	WORKER_LIST_FRAGMENT,
	WORKER_OUTREACH_FRAGMENT,
	WORKER_REFERENCE_FRAGMENT,
	WORKING_AUTHORIZATION_FRAGMENT,
} from '../apollo.fragment'

export const REFRESH_WORKER_TOKEN = gql`
	mutation refreshToken($refreshToken: String!) {
		refreshToken(refreshToken: $refreshToken) {
			authToken {
				accessToken
				refreshToken
				tokenType
				expiresIn
			}
		}
	}
`

export const GET_USER_INFO_QUERY = gql`
	query getUserInfo {
		employer {
			...maestroEmployerFragment
		}
	}

	${MAESTRO_EMPLOYER_FRAGMENT}
`

export const GET_RECRUITER_WORKER_DETAIL_QUERY = gql`
	query recruiterWorkerDetail($id: String!) {
		worker(id: $id) {
			...workerFragment
			workingAuthorization {
				...workingAuthorizationFragment
			}
			workExperiences {
				...workExperienceFragment
			}
			workerCertifications {
				...workerCertificationFragment
			}
			workerEmergencyContacts {
				...emergencyContactFragment
			}
			workerReferences {
				...workerReferenceFragment
			}
			workerEducations {
				...workerEducationFragment
			}
			workerDisciplineSpecialties {
				...workerDisciplineSpecialtyFragment
			}
			workingPreferredLocations {
				city
				country
				externalId
				id
				isPrimary
				state
				stateName
			}
		}
	}

	${WORKER_FRAGMENT}
	${WORKER_EDUCATION_FRAGMENT}
	${WORKING_AUTHORIZATION_FRAGMENT}
	${WORK_EXPERIENCE_FRAGMENT}
	${WORKER_CERTIFICATION_FRAGMENT}
	${EMERGENCY_CONTACT_FRAGMENT}
	${WORKER_REFERENCE_FRAGMENT}
	${WORKER_DISCIPLINE_SPECIALTY_FRAGMENT}
`

export const GET_WORKER_DETAIL_FOR_TIMECARDS_QUERY = gql`
	query getWorkerDetailTimeQuery($id: String!) {
		worker(id: $id) {
			...workerFragment
			company {
				salesforceIntegration {
					...salesforceIntegrationFragment
				}
			}
			workedTimesheets {
				...workedTimesheetFragment
				timesheetImages {
					imageUrls(size: ["100x100", "200x200"])
					...imageFileFragment
				}
			}
		}
	}

	${WORKER_FRAGMENT}
	${SALESFORCE_INTEGRATION_FRAGMENT}
	${WORKED_TIMESHEET_FRAGMENT}
	${IMAGE_FILE_FRAGMENT}
`

export const GET_CREDENTIALING_URL_QUERY = gql`
	query getWorkerDetailCredentialingQuery($id: String!) {
		worker(id: $id) {
			id
			credentialingUrl
		}
	}
`

export const GET_WORKER_CERTIFICATIONS_QUERY = gql`
	query getWorkerCertificationsQuery($id: String!) {
		workerCertifications(workerId: $id) {
			...workerCertificationFragment
			licenseImages {
				...imageFileFragment
			}
		}
	}

	${WORKER_CERTIFICATION_FRAGMENT}
	${IMAGE_FILE_FRAGMENT}
`

export const GET_WORKER_CREDENTIALS_QUERY = gql`
	query getWorkerCredentialsQuery($filter: WorkerCredentialStatusEnum, $after: String, $before: String, $first: Int, $last: Int) {
		workerCredentials(filter: $filter, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...workerCredentialFragment
			}
		}
	}

	${WORKER_CREDENTIAL_FRAGMENT}
`

export const GET_WORKER_SOCIAL_QUERY = gql`
	query getWorkerSocialQuery {
		workerSocialAuthentications {
			providerName
			id
			providerUuid
			email
		}
	}
`

export const SEARCH_WORKERS_QUERY = gql`
	query searchWorkersQuery(
		$filter: MaestroWorkerFilter
		$sortBy: MaestroRecruiterSort
		$after: String
		$before: String
		$first: Int
		$last: Int
		$summaryRequire: Boolean
	) {
		searchWorkers(filter: $filter, sortBy: $sortBy, after: $after, before: $before, first: $first, last: $last, summaryRequire: $summaryRequire) {
			totalCount
			pageInfo {
				hasPreviousPage
				hasNextPage
				endCursor
				startCursor
			}
			nodes {
				...workerListFragment
			}
		}
	}

	${WORKER_LIST_FRAGMENT}
`

// SEARCH_WORKERS_QUERY plus `contacted` / `lastContactedAt` (see WORKER_OUTREACH_FRAGMENT).
// V2 screens only: a gem without Maestro V2 rejects the whole document for those two fields.
export const SEARCH_WORKERS_WITH_OUTREACH_QUERY = gql`
	query searchWorkersWithOutreachQuery(
		$filter: MaestroWorkerFilter
		$sortBy: MaestroRecruiterSort
		$after: String
		$before: String
		$first: Int
		$last: Int
		$summaryRequire: Boolean
	) {
		searchWorkers(filter: $filter, sortBy: $sortBy, after: $after, before: $before, first: $first, last: $last, summaryRequire: $summaryRequire) {
			totalCount
			pageInfo {
				hasPreviousPage
				hasNextPage
				endCursor
				startCursor
			}
			nodes {
				...workerListFragment
				...workerOutreachFragment
			}
		}
	}

	${WORKER_LIST_FRAGMENT}
	${WORKER_OUTREACH_FRAGMENT}
`

/*
 * Sends (or re-sends) the candidate portal welcome email for a worker who already
 * exists in Maestro.
 *
 * The employer-side twin is RESEND_MAESTRO_INVITE in recruiter.query.js; this one
 * exists because Han Staff's weekly "NEW APP LIST" is a list of CANDIDATES who fall
 * outside the automated welcome-email component, and the only path to inviting them
 * was a support ticket.
 *
 * `$workerId: String!`, not ID — the BE mutation declares `argument :worker_id, String`,
 * same as resendMaestroInvite's employerId.
 *
 * The selection is the four `user` timestamps the Sign-in column reads, so a
 * successful send updates the row through the Apollo cache even before the list
 * refetches.
 */
export const RESEND_WORKER_INVITE = gql`
	mutation resendWorkerInvite($workerId: String!) {
		resendWorkerInvite(workerId: $workerId) {
			id
			email
			user {
				id
				lastSignInAt
				welcomeEmailSentAt
				tempPasswordExpiredAt
				changePasswordRequired
			}
		}
	}
`

// Lightweight worker search for filter autocompletes (Candidate column filter).
// Returns only the fields needed to render an option label — avoids the heavy
// WORKER_FRAGMENT that SEARCH_WORKERS_QUERY pulls on every keystroke.
export const SEARCH_WORKER_OPTIONS_QUERY = gql`
	query searchWorkerOptionsQuery($filter: MaestroWorkerFilter, $sortBy: MaestroRecruiterSort, $first: Int) {
		searchWorkers(filter: $filter, sortBy: $sortBy, first: $first) {
			nodes {
				id
				fullName
				email
			}
		}
	}
`

export const RECRUITER_WORKER_ON_ASSIGNMENTS_QUERY = gql`
	query recruiterWorkerOnAssignmentsQuery($sort: SortArgument, $after: String, $before: String, $first: Int, $last: Int) {
		recruiterWorkerOnAssignments(sort: $sort, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...workerAssignmentFragment
			}
		}
	}

	${WORKER_ASSIGNMENT_FRAGMENT}
`

/*
 * `nodeExtra` adds fields to each assignment node. A field the server does not
 * know fails validation even under `@include(if: false)`, so a field that is
 * not on every environment gets its own document instead of a directive.
 */
const recruiterWorkerAssignmentsQuery = (nodeExtra = '') => gql`
	query recruiterWorkerAssignmentsQuery(
		$sortBy: MaestroRecruiterSort
		$filter: MaestroWorkerAssignmentFilter
		$after: String
		$before: String
		$first: Int
		$last: Int
	) {
		workerAssignments(sortBy: $sortBy, filter: $filter, after: $after, before: $before, first: $first, last: $last) {
			nodes {
				id
				worker {
					id
					engagementScore
					fullName
					firstName
					lastName
					email
					phone
					user {
						id
					}
					externalFriendlyId
					avatar {
						file {
							fileUrl
						}
					}
				}
				job {
					id
					marginPercentage
					billRate
					externalFriendlyId
					jobGeneral {
						id
						title
						hotJob
						settings
						externalId
						workTypes
						address {
							city
							state
							zipcode
							country
						}
					}
					facility {
						id
						name
					}
				}
				retentionStatus
				scheduledStartTime
				scheduledEndTime
				${nodeExtra}
			}
			totalCount
			pageInfo {
				hasPreviousPage
				hasNextPage
				endCursor
				startCursor
			}
		}
	}
`

export const RECRUITER_JOB_WORKER_ASSIGNMENTS_QUERY = recruiterWorkerAssignmentsQuery()

/*
 * Ending soon on the V2 board: also whether the clinician already has their next
 * assignment. BE resolves the field for the whole page in one extra search, and
 * only when it is selected — so only the list that shows it asks for it.
 */
export const RECRUITER_JOB_WORKER_ASSIGNMENTS_NEXT_STEP_QUERY = recruiterWorkerAssignmentsQuery('hasNextAssignment')

export const RECRUITER_SEARCH_JOBS_QUERY = gql`
	query recruiterSearchJobsQuery($filter: MaestroRecruiterJobFilter, $sortBy: MaestroRecruiterSort, $after: String, $before: String, $first: Int, $last: Int) {
		searchJobs(filter: $filter, sortBy: $sortBy, after: $after, before: $before, first: $first, last: $last) {
			nodes {
				id
				externalFriendlyId
				# Optional columns (see job-optional-columns.js): off by default, offered
				# in Table settings. Scalars only — everything expensive on a job row is
				# behind jobGeneral / employer, and none of it is here.
				payRate
				payType
				payAmountMin
				payAmountMax
				estimatedGrossPayRange
				hoursPerWeek
				shiftLength
				duration
				durationType
				remainingSlotsNumber
				accountManagerName
				region
				timezone
				status
				atsStatus
				atsSource
				isPerDiem
				hasBonus
				viewedCount
				publishedAt
				externalCreatedAt
				jobGeneral {
					title
					externalId
					address {
						city
						state
						zipcode
						country
					}
					disciplines {
						id
						disciplineName
					}
					specialties {
						id
						name
					}
					settings
					workTypes
				}
				facility {
					id
					name
				}
				jobApplicantsCount
				workerAssignmentsCount
				submissionReadyCount
				slotsNumber
				internalSub
				externalSub
				noTalentMatches
				startDatetime
				endDatetime
				startDatetime
				marginPercentage
				billRate
			}
			totalCount
			pageInfo {
				hasPreviousPage
				hasNextPage
				endCursor
				startCursor
			}
		}
	}
`

export const GET_RECRUITER_JOB_DETAIL_QUERY = gql`
	query getRecruiterJob($jobId: String!) {
		job(id: $jobId) {
			...recruiterJobFragment
		}
	}

	${RECRUITER_JOB_FRAGMENT}
`

export const GET_RECRUITER_JOB_APPLICANT_ACTIVITIES_QUERY = gql`
	query getRecruiterJobApplicantActivities($trackableType: String!, $workerId: String!, $trackableId: String!) {
		activities(trackableType: $trackableType, workerId: $workerId, trackableId: $trackableId) {
			id
			key
			message
			owner {
				id
				firstName
				lastName
				fullName
			}
			trackable {
				id
				job {
					id
					title
				}
			}
			createdAt
		}
	}
`

export const GET_RECRUITER_JOB_APPLICANT_QUERY = gql`
	query getRecruiterJobApplicant($id: String!) {
		recruiterJobApplicant(id: $id) {
			...jobApplicantFragment
		}
	}

	${JOB_APPLICANT_FRAGMENT}
`

export const GET_RECRUITER_JOB_DETAIL_TITLE_QUERY = gql`
	query getRecruiterJob($jobId: String!) {
		job(id: $jobId) {
			jobGeneral {
				title
				workTypes
			}
		}
	}
`

export const GET_RECRUITER_JOB_APPLICANTS_QUERY = gql`
	query recruiterJobApplicantsQuery(
		$workerId: String
		$jobId: String
		$jobApplicantScope: String
		$sort: SortArgument
		$after: String
		$before: String
		$first: Int
		$last: Int
	) {
		recruiterJobApplicants(
			workerId: $workerId
			jobId: $jobId
			jobApplicantScope: $jobApplicantScope
			sort: $sort
			after: $after
			before: $before
			first: $first
			last: $last
		) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...jobApplicantFragment
			}
		}
	}

	${JOB_APPLICANT_FRAGMENT}
`

export const GET_RECRUITER_JOB_APPLICANT_WORKERS_QUERY = gql`
	query recruiterJobApplicantWorkersQuery(
		$keyword: String
		$filterScope: String
		$sort: SortArgument
		$after: String
		$before: String
		$first: Int
		$last: Int
	) {
		recruiterJobApplicantWorkers(keyword: $keyword, filterScope: $filterScope, sort: $sort, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...workerFragment
			}
		}
	}

	${WORKER_FRAGMENT}
`

export const GET_RECRUITER_WORKER_JOB_APPLICANTS_QUERY = gql`
	query recruiterWorkerJobApplicantsQuery(
		$workerId: String
		$jobApplicantScope: MaestroJobApplicantScopeEnum
		$filterScope: MaestroJobApplicantFilterScopeEnum
		$sort: SortArgument
		$first: Int
	) {
		recruiterWorkerJobApplicants(
			filter: { workerId: $workerId, jobApplicantScope: $jobApplicantScope, filterScope: $filterScope }
			sort: $sort
			first: $first
		) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				id
				stage
				stageName
				applyDate
				submittal {
					id
					status
					submittalDate
				}
				job {
					id
					title
					slotsNumber
					matchingPercentage
					matchingExplanation
					matchingExplanationDetails {
						text
						level
					}
					remainingSlotNumbers
				}
			}
		}
	}
`

export const SEARCH_JOB_APPLICANTS_QUERY = gql`
	query searchJobApplicantsQuery(
		$sortBy: MaestroRecruiterSort
		$after: String
		$before: String
		$first: Int
		$last: Int
		$filter: MaestroRecruiterJobApplicantFilter
		$summaryRequire: Boolean
	) {
		searchJobApplicants(filter: $filter, sortBy: $sortBy, after: $after, before: $before, first: $first, last: $last, summaryRequire: $summaryRequire) {
			totalCount
			pageInfo {
				hasPreviousPage
				hasNextPage
				endCursor
				startCursor
			}
			nodes {
				id
				stage
				submittedDatetime
				status
				job {
					id
					jobGeneral {
						title
						workTypes
						settings
						address {
							city
							state
							zipcode
							country
						}
					}
					externalFriendlyId
					slotsNumber
					region
					facility {
						name
					}
					matchingPercentage
					matchingExplanation
					matchingExplanationDetails {
						text
						level
					}
				}
			}
		}
	}
`

export const GET_RECRUITER_WORKER_JOB_MATCHES_FULL_QUERY = gql`
	query recruiterWorkerJobMatchesFullQuery($workerId: String!, $sort: SortArgument, $after: String, $before: String, $first: Int, $last: Int) {
		recruiterWorkerJobMatches(workerId: $workerId, sort: $sort, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...jobFragment
			}
		}
	}

	${JOB_FRAGMENT}
`

export const GET_RECRUITER_WORKER_JOB_APPLICANTS_FULL_QUERY = gql`
	query recruiterWorkerJobApplicantsQuery(
		$workerId: String
		$jobApplicantScope: MaestroJobApplicantScopeEnum
		$filterScope: MaestroJobApplicantFilterScopeEnum
		$sortBy: MaestroRecruiterSort
		$after: String
		$before: String
		$first: Int
		$last: Int
	) {
		searchJobApplicants(
			filter: { workerId: $workerId, jobApplicantScope: $jobApplicantScope, filterScope: $filterScope }
			sortBy: $sortBy
			after: $after
			before: $before
			first: $first
			last: $last
		) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				id
				stage
				status
				job {
					...recruiterJobFragment
				}
			}
		}
	}

	${RECRUITER_JOB_FRAGMENT}
`

export const GET_RECRUITER_WORKER_BOOKMARKED_JOBS_QUERY = gql`
	query recruiterWorkerBookmarkedJobsQuery($workerId: String!, $after: String, $before: String, $first: Int, $last: Int) {
		favoritedJobs(workerId: $workerId, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...recruiterJobBaseFragment
			}
		}
	}

	${RECRUITER_JOB_BASE_FRAGMENT}
`

export const GET_RECRUITER_WORKER_LAST_VIEWED_JOBS_QUERY = gql`
	query getRecruiterWorkerLastViewedJobs($activityType: JobActivityTypeEnum, $workerId: String!, $after: String, $before: String, $first: Int, $last: Int) {
		recruiterWorkerLastViewedJobs(workerId: $workerId, activityType: $activityType, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				id
				location {
					region
					city
					zipcode
					addressState
				}
				customStartDate
				createdAt
				discipline
				duration
				facilityType
				hospital
				id
				jobType
				externalCreatedAt
				matchingPercentage
				matchingExplanation
				matchingExplanationDetails {
					text
					level
				}
				minWeeklyPayAmount
				shifts
				skillLongName
				specialty
				startDate
				status
				title
				updatedAt
				isAsap
				viewCount
				weeklyPayAmount
				slotsNumber
			}
		}
	}
`

// Candidate profile › Jobs › "Viewed": jobs the candidate opened but has neither
// applied to nor favorited, newest view first. The BE does the exclusion.
// `lastViewedAt` is this candidate's last view; `viewCount` is the job's view count
// across ALL candidates (jobs.viewed_count) — there is no per-candidate count.
export const GET_RECRUITER_WORKER_VIEWED_JOBS_QUERY = gql`
	query getRecruiterWorkerViewedJobs($workerId: String!, $after: String, $first: Int) {
		recruiterWorkerLastViewedJobs(workerId: $workerId, after: $after, first: $first) {
			totalCount
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				id
				status
				lastViewedAt
				viewCount
				startDatetime
				endDatetime
				duration
				durationType
				slotsNumber
				shifts
				payAmountMax
				payAmountMin
				payType
				matchingPercentage
				matchingExplanation
				matchingExplanationDetails {
					text
					level
				}
				jobGeneral {
					...jobDetailGeneralFragment
				}
			}
		}
	}

	${JOB_DETAIL_GENERAL_FRAGMENT}
`

export const GET_RECRUITER_WORKER_RECOMMENDED_JOBS_QUERY = gql`
	query recruiterWorkerSuggestionJobs($workerId: String!, $after: String, $before: String, $first: Int, $last: Int) {
		suggestionJobs(workerId: $workerId, after: $after, before: $before, first: $first, last: $last) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...recruiterJobBaseFragment
			}
		}
	}

	${RECRUITER_JOB_BASE_FRAGMENT}
`

export const GET_RECRUITER_WORKER_SUBMITTED_JOBS_QUERY = gql`
	query searchJobApplicants(
		$after: String
		$before: String
		$first: Int
		$last: Int
		$filter: MaestroRecruiterJobApplicantFilter
		$sortBy: MaestroRecruiterSort
	) {
		searchJobApplicants(after: $after, before: $before, first: $first, last: $last, filter: $filter, sortBy: $sortBy) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				atsStatus
				payRate
				salary
				stage
				status
				job {
					...recruiterJobFragment
				}
			}
		}
	}

	${RECRUITER_JOB_FRAGMENT}
`

export const GET_RECRUITER_WORKER_ACTIVITIES_QUERY = gql`
	query recruiterWorkerActivities(
		$workerId: String!
		$activityTypes: [MaestroWorkerActivityTypeEnum!]
		$after: String
		$before: String
		$first: Int
		$last: Int
	) {
		workerActivities(workerId: $workerId, activityTypes: $activityTypes, after: $after, before: $before, first: $first, last: $last) {
			nodes {
				id
				key
				createdAt
				# owner is about to stop being a MaestroWorker.
				#
				# SUP-3003 on the backend splits WHO PERFORMED an activity from WHO IT IS
				# ABOUT: owner becomes the actor and is retyped to a union of MaestroWorker
				# (candidate behaviour) and MaestroEmployer (internal actions). A union
				# cannot be selected on directly, so the plain owner selection this used to
				# send stops validating the moment that gem reaches an environment - and a
				# document that fails validation returns no data at all, so the tab goes
				# blank rather than degrading.
				#
				# The inline fragment is valid against BOTH schemas: today owner is a
				# MaestroWorker and a fragment on its own type is legal, and afterwards it is
				# one member of the union. That is what lets this ship ahead of the backend
				# rather than in lockstep with it.
				#
				# The MaestroEmployer half is deliberately absent: spreading it inside
				# today's MaestroWorker is itself a validation error. It goes in once the
				# backend change is live, and internal actors light up then.
				owner {
					__typename
					... on MaestroWorker {
						id
						fullName
						firstName
						lastName
					}
				}
				message
				eventTime
				trackable {
					__typename
					... on MaestroJobApplicant {
						id
						job {
							id
							jobGeneral {
								title
								workTypes
							}
						}
					}
					... on MaestroJob {
						id
						jobGeneral {
							title
							workTypes
						}
					}
				}
			}
			totalCount
			pageInfo {
				endCursor
				hasNextPage
				hasPreviousPage
				pageSize
				startCursor
			}
		}
	}
`

export const GET_RECRUITER_WORKER_LIFECYCLE_QUERY = gql`
	query recruiterWorkerLifecycle($workerId: String!) {
		workerLifecycle(workerId: $workerId) {
			occurredAt
			category
			title
			detail
			icon
			link
		}
	}
`

export const GET_WORKER_JOB_MATCHES_QUERY = gql`
	query recruiterWorkerJobMatchesQuery($workerId: String!, $sort: SortArgument, $first: Int) {
		recruiterWorkerJobMatches(workerId: $workerId, sort: $sort, first: $first) {
			totalCount
			nodes {
				id
				title
				startDate
				customStartDate
				exclusiveJob
				slotsNumber
				remainingSlotNumbers
				matchingPercentage
				matchingExplanation
				matchingExplanationDetails {
					text
					level
				}
				isAsap
			}
		}
	}
`

export const GET_JOB_MATCHES_BY_WORKER_QUERY = gql`
	query jobMatchesByWorkerQuery(
		$workerId: String!
		$sortBy: MaestroRecruiterSort
		$after: String
		$before: String
		$first: Int
		$last: Int
		$filter: MaestroRecruiterJobFilter
		$summaryRequire: Boolean
	) {
		jobMatchesByWorker(
			workerId: $workerId
			filter: $filter
			sortBy: $sortBy
			first: $first
			last: $last
			before: $before
			after: $after
			summaryRequire: $summaryRequire
		) {
			totalCount
			pageInfo {
				hasNextPage
				endCursor
			}
			nodes {
				id
				jobGeneral {
					id
					title
					workTypes
					address {
						city
						state
						stateName
						zipcode
						country
					}
				}
				facility {
					id
					name
				}
				externalFriendlyId
				startDatetime
				slotsNumber
				billRate
				accountManagerName
				estimatedGrossPayRange
				matchingPercentage
				matchingExplanation
				matchingExplanationDetails {
					text
					level
				}
				matchFeedbackAction
			}
		}
	}
`

export const GET_RECRUITER_STATISTICS_QUERY = gql`
	query recruiterStatisticsQuery {
		statistics {
			jobsTotal
			jobsOpenTotal
			jobsClosedTotal
			workersTotal
			toasTotalCurrentPeriod
			toasTrendingPercentage
			availableTravelersTotalCurrentPeriod
			availableTravelersTrendingPercentage
			jobsTotalHasJobApplicant
			matchesTotalByWorker
			matchesTotalByJob
			qualityMatchesTrendingPercentage
			qualityMatchesTotalCurrentPeriod
			workersTotalHasSubmissionReady
			dashboardJobApplicantsTotal
			jobApplicantsTotal
			employersTotal
			preSubmissionsTotalByWorkers
			workerAssignmentsActiveTotal
		}
	}
`

export const GET_RECRUITER_DASHBOARD_STATISTICS_QUERY = gql`
	query recruiterDashboardStatisticsQuery {
		statistics {
			toasTotalCurrentPeriod
			toasTrendingPercentage
			availableTravelersTotalCurrentPeriod
			availableTravelersTrendingPercentage
			qualityMatchesTrendingPercentage
			qualityMatchesTotalCurrentPeriod
			dashboardJobApplicantsTotal
			matchesTotalByWorker
			preSubmissionsTotalByWorkers
			totalJobFavoritesByLike
			totalJobFavoritesByUnlike
		}
	}
`

export const GET_LEFT_SIDE_RECRUITER_STATISTICS_QUERY = gql`
	query recruiterLeftSideStatisticsQuery {
		statistics {
			jobsTotal
			workersTotal
			employersTotal
			# The Candidates Matches tab badge. V1 reads the same field from its own
			# statistics query; the shell query is what V2 already has in hand.
			matchesTotalByWorker
			# The Submissions tab badge on /v2/candidates and /v2/jobs: Classic's
			# "Active Submission" count, the same field and BE cache key as V1.
			dashboardJobApplicantsTotal
		}
	}
`

export const GET_RECRUITER_JOB_STATISTICS_QUERY = gql`
	query recruiterJobStatisticsQuery {
		statistics {
			workerAssignmentsActiveTotal
			workerAssignmentsUpcomingTotal
			jobsOpenTotal
			jobsTotalHasJobApplicant
			matchesTotalByJob
			jobsClosedTotal
			totalJobFavoritesByLike
			totalJobFavoritesByUnlike
			totalWorkerFavoritesByLike
			totalWorkerFavoritesByUnlike
		}
	}
`

/**
 * Row counts for the two halves of the open-jobs list, in one round trip.
 *
 * The `statistics` payload only carries `jobsOpenTotal`, which counts per diem
 * and standard jobs together, so once the Shift Jobs tab splits them it can
 * label neither tab. Deriving one count by subtracting the other would drift
 * from the lists themselves for any job whose `isPerDiem` is unset, so both
 * sides are counted with the same predicate each tab actually queries with.
 *
 * `first: 1` because only `totalCount` is read — the node is the smallest
 * page the connection will return.
 */
export const RECRUITER_OPEN_JOBS_SPLIT_COUNTS_QUERY = gql`
	query recruiterOpenJobsSplitCountsQuery($standardFilter: MaestroRecruiterJobFilter, $perDiemFilter: MaestroRecruiterJobFilter) {
		standardJobs: searchJobs(filter: $standardFilter, first: 1) {
			totalCount
		}
		perDiemJobs: searchJobs(filter: $perDiemFilter, first: 1) {
			totalCount
		}
	}
`

export const GET_RECRUITER_CANDIDATE_STATISTICS_QUERY = gql`
	query recruiterCandidateStatisticsQuery {
		statistics {
			workerAssignmentsActiveTotal
			dashboardJobApplicantsTotal
			workersTotalHasSubmissionReady
			matchesTotalByWorker
			preSubmissionsTotalByWorkers
			totalJobFavoritesByLike
			totalJobFavoritesByUnlike
			workersTotalPerDiemEligible
		}
	}
`

export const GET_JOB_STATISTICS_QUERY = gql`
	query jobStatistics($jobId: String!) {
		jobStatistics(jobId: $jobId) {
			jobApplicantsTotal
			workerAssignmentsTotal
			workerMatchesTotal
			workerMatchesByLikeTotal
			workerMatchesByUnlikeTotal
		}
	}
`

export const GET_RECRUITER_WORKER_STATISTICS_QUERY = gql`
	query workerStatistics($workerId: String!) {
		workerStatistics(workerId: $workerId) {
			jobApplicantsTotal
			jobMatchesTotal
			workerAssignmentsTotal
		}
	}
`

export const GET_MASTER_DATA = gql`
	query getMasterData {
		appInit {
			masterData
		}
	}
`

export const WORKER_ON_ASSIGNMENTS_QUERY = gql`
	query workerOnAssignmentsQuery(
		$filter: MaestroWorkerAssignmentFilter
		$sortBy: MaestroRecruiterSort
		$after: String
		$before: String
		$first: Int
		$last: Int
	) {
		workerAssignments(sortBy: $sortBy, filter: $filter, after: $after, before: $before, first: $first, last: $last) {
			nodes {
				id
				worker {
					id
					externalFriendlyId
					avatar {
						file {
							fileUrl
						}
					}
					addresses {
						city
						state
						stateName
						zipcode
						country
					}
					email
					fullName
					lastName
					firstName
					engagementScore
					profilePercentageCompleted
					employer {
						avatar {
							file {
								fileUrl
							}
						}
						email
						firstName
						fullName
						id
						lastName
					}
					# Staffing Specialist — see WORKER_FRAGMENT. This query hand-rolls
					# the worker selection instead of reusing the fragment, so the
					# field has to be repeated here.
					secondEmployer {
						avatar {
							file {
								fileUrl
							}
						}
						email
						firstName
						fullName
						id
						lastName
					}
					recentActivities {
						createdAt
						eventTime
						id
						key
						message
					}
				}
				atsSource
				marginPercentage
				retentionStatus
				job {
					id
					marginPercentage
					billRate
					externalFriendlyId
					jobGeneral {
						id
						title
						settings
						workTypes
						address {
							city
							state
							zipcode
							country
						}
					}
					facility {
						id
						name
						facilityGroup {
							id
							name
						}
						parent {
							id
							name
						}
						facilityTypes
					}
				}
				scheduledStartTime
				scheduledEndTime
			}

			totalCount
			pageInfo {
				hasPreviousPage
				hasNextPage
				endCursor
				startCursor
			}
		}
	}
`

export const RECRUITER_SEARCH_CANDIDATES_QUERY = gql`
	query searchWorkers($filter: MaestroWorkerFilter, $first: Int, $after: String) {
		searchWorkers(filter: $filter, first: $first, after: $after) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				...workerFragment
			}
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
		}
	}
	${WORKER_FRAGMENT}
`

export const RECRUITER_WORKER_ASSIGNMENTS_JOBS_INFO_QUERY = gql`
	query($sortBy: MaestroRecruiterSort, $after: String, $before: String, $first: Int, $last: Int, $filter: MaestroWorkerAssignmentFilter) {
		workerAssignments(filter: $filter, sortBy: $sortBy, after: $after, before: $before, first: $first, last: $last) {
			nodes {
				id
				worker {
					id
					engagementScore
					fullName
					firstName
					lastName
					email
				}
				job {
					id
					marginPercentage
					jobGeneral {
						id
						title
						workTypes
						address {
							city
							state
							zipcode
							country
						}
					}
					facility {
						id
						name
					}
				}
				stage
				status
				scheduledStartTime
				scheduledEndTime
			}
			totalCount
			pageInfo {
				hasPreviousPage
				hasNextPage
				endCursor
				startCursor
			}
		}
	}
`

export const RECRUITER_JOB_WORKER_MATCHES_QUERY = gql`
	query recruiterJobWorkerMatchesQuery(
		$jobId: String!
		$sortBy: MaestroRecruiterSort
		$before: String
		$after: String
		$first: Int
		$last: Int
		$filter: MaestroWorkerFilter
	) {
		workerMatchesByJob(jobId: $jobId, sortBy: $sortBy, before: $before, after: $after, first: $first, last: $last, filter: $filter) {
			totalCount
			pageInfo {
				hasNextPage
				hasPreviousPage
				endCursor
			}
			nodes {
				id
				fullName
				firstName
				lastName
				email
				phone
				user {
					id
				}
				matchingPercentage
				matchingExplanation
				matchingExplanationDetails {
					text
					level
				}
				externalFriendlyId
				licenseStates
				atsStatus
				engagementScore
				recentActivities {
					createdAt
					eventTime
					id
					key
					message
				}
				workerDisciplineSpecialties {
					discipline {
						id
						disciplineName
					}
					specialty {
						id
						name
						longName
					}
				}
				workerDisciplines {
					id
					discipline {
						disciplineName
						id
					}
				}
				matchFeedbackAction
				employer {
					avatar {
						file {
							fileUrl
						}
					}
					email
					firstName
					fullName
					id
					lastName
					phone
					title
				}
				# Staffing Specialist — see WORKER_FRAGMENT.
				secondEmployer {
					avatar {
						file {
							fileUrl
						}
					}
					email
					firstName
					fullName
					id
					lastName
					phone
					title
				}
			}
		}
	}
`

const JOB_INFO_APPLICANTS_NODE_FIELDS = `
	id
	worker {
		id
		engagementScore
		fullName
		firstName
		lastName
		email
		phone
		user {
			id
		}
		profilePercentageCompleted
		matchingPercentage
		matchingExplanation
		matchingExplanationDetails {
			text
			level
		}
		licenseStates
		recentActivities {
			createdAt
			eventTime
			id
			key
			message
		}
	}
	submittedDatetime
	appliedDate
	appliedDatetime
	startDatetime
	stage
`

const jobInfoApplicantsQuery = (operationName, extraNodeFields = '', withSummary = false) => gql`
	query ${operationName}(
		$filter: MaestroRecruiterJobApplicantFilter
		$sortBy: MaestroRecruiterSort
		$after: String
		$before: String
		$first: Int
		$last: Int
		${withSummary ? ', $summaryRequire: Boolean' : ''}
	) {
		searchJobApplicants(
			filter: $filter
			after: $after
			before: $before
			sortBy: $sortBy
			first: $first
			last: $last
			${withSummary ? ', summaryRequire: $summaryRequire' : ''}
		) {
			nodes {
				${JOB_INFO_APPLICANTS_NODE_FIELDS}
				${extraNodeFields}
			}
			totalCount
			${withSummary ? 'summaryResults' : ''}
			pageInfo {
				hasPreviousPage
				hasNextPage
				endCursor
				startCursor
			}
		}
	}
`

export const GET_RECRUITER_WORKER_JOB_APPLICANTS_JOB_INFO_QUERY = jobInfoApplicantsQuery('recruiterWorkerJobApplicantsQuery')

/*
 * Same table with SPT-2326 tracking. A separate document, not `@include`: a backend
 * whose gem predates `applicationTracking` rejects the whole query when the field
 * is merely named, so a tenant without the flag must never send it.
 */
export const GET_RECRUITER_WORKER_JOB_APPLICANTS_JOB_INFO_TRACKING_QUERY = jobInfoApplicantsQuery(
	'recruiterWorkerJobApplicantsTrackingQuery',
	`applicationTracking {
		refSource
		refId
		refCampaign
		originalSource
		utmSource
		utmMedium
		utmCampaign
		utmContent
		utmTerm
	}`,
	true
)

export const GET_JOB_DETAIL_STATISTICS = gql`
	query($jobId: String!) {
		recruiterJobStatistics(jobId: $jobId) {
			assignmentsTotal
			submissionsTotal
			matchesTotal
		}
	}
`

export const GET_TOAS_STATISTICS_QUERY = gql`
	query recruiterToasStatisticsQuery {
		statistics {
			toasTotalCurrentPeriod
			toasTrendingPercentage
		}
	}
`

export const GET_AVAILABLE_CANDIDATES_STATISTICS_QUERY = gql`
	query recruiterAvailableCandidatesStatisticsQuery {
		statistics {
			availableTravelersTotalCurrentPeriod
			availableTravelersTrendingPercentage
		}
	}
`

export const GET_QUALITY_MATCHES_STATISTICS_QUERY = gql`
	query recruiterQualityMatchesStatisticsQuery {
		statistics {
			qualityMatchesTotalCurrentPeriod
			qualityMatchesTrendingPercentage
			qualityMatchesSyncedAt
		}
	}
`

export const GET_DASHBOARD_JOB_APPLICANTS_STATISTICS_QUERY = gql`
	query recruiterDashboardJobApplicantsStatisticsQuery {
		statistics {
			dashboardJobApplicantsTotal
			preSubmissionsTotalByWorkers
		}
	}
`

export const GET_PREFERRED_ORIENTED_FACILITIES_QUERY = gql`
	query GetPreferredOrientedFacilities($workerId: String!, $first: Int, $after: String) {
		facilities(first: $first, after: $after, filter: { workerId: $workerId, statusPreferred: true, isOriented: true }) {
			nodes {
				id
				name
				description
				active
				regionName
				facilityTypes
				perDiemShiftOpenings
				address {
					id
					city
					state
					stateName
					country
					zipcode
					street
					aptNumber
				}
			}
			pageInfo {
				hasNextPage
				hasPreviousPage
				startCursor
				endCursor
			}
			totalCount
		}
	}
`

export const GET_RECRUITER_WORKER_ACTIVITY_TYPE_COUNTS_QUERY = gql`
	query recruiterWorkerActivityTypeCounts($workerId: String!) {
		workerActivityTypeCounts(workerId: $workerId) {
			type
			label
			count
		}
	}
`
